import {
  LOCKUP_VIEWBOX, WORDMARK_VIEWBOX, WAVE_GROUP_TRANSFORM, WORD_GROUP_TRANSFORM,
  WAVE_TOP, WAVE_BOTTOM, WORD_INK, WORD_ACCENT,
  STACK_VIEWBOX, STACK_TRANSFORMS, STACK_PATHS,
} from "../../brand/logo-paths.js";

/**
 * Flow Books logo, per the brand sheet.
 *
 * The wordmark reads "flow books": "flow" in ink, "books" in the accent, the
 * same two-colour split the Flow Inc. mark used for "inc.". Both words are one
 * continuous Playfair Display 700 setting — see brand/logo-paths.js.
 *
 * The two-rust rule: rust #C25B33 is the accent on light backgrounds. On
 * charcoal it lacks contrast, so every accent switches to soft terracotta
 * #D98A63. `tone` picks the correct pair — never mix them on one surface.
 *
 *   tone="light"  charcoal ink + rust accent      (cream backgrounds)
 *   tone="dark"   cream ink + terracotta accent   (charcoal backgrounds)
 *   tone="mono"   single colour throughout        (photography, tight spaces)
 *
 * Every viewBox here comes from brand/logo-paths.js rather than being written
 * out again, so re-setting the wordmark can never leave a stale crop behind.
 */
import { COLORS, tone as toneOf } from "../../brand/tokens.js";

const TONES = {
  light: toneOf("light"),
  dark: toneOf("dark"),
  mono: { ink: "currentColor", accent: "currentColor" },
};

const NAME = "Flow Books";

/** The wave mark on its own — app icon, favicon, PDF header, tight spaces. */
export function FlowMark({ size = 28, tone = "light", title }) {
  const { ink, accent } = TONES[tone] || TONES.light;
  return (
    <svg
      width={size}
      height={size}
      viewBox="-2 -5 106 106"
      role={title ? "img" : "presentation"}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {/* The wave is 102x59 in lockup space; the viewBox pads it to a centred square. */}
      <g transform={WAVE_GROUP_TRANSFORM}>
        <path d={WAVE_TOP} fill={ink} />
        <path d={WAVE_BOTTOM} fill={accent} />
      </g>
    </svg>
  );
}

/** Primary horizontal lockup: wave icon + wordmark. */
export function FlowLockup({ height = 24, tone = "light", title = NAME }) {
  const { ink, accent } = TONES[tone] || TONES.light;
  return (
    <svg
      height={height}
      viewBox={LOCKUP_VIEWBOX}
      role="img"
      aria-label={title}
      style={{ display: "block", width: "auto" }}
    >
      <g transform={WAVE_GROUP_TRANSFORM}>
        <path d={WAVE_TOP} fill={ink} />
        <path d={WAVE_BOTTOM} fill={accent} />
      </g>
      <g transform={WORD_GROUP_TRANSFORM}>
        <path d={WORD_INK} fill={ink} fillRule="nonzero" />
        <path d={WORD_ACCENT} fill={accent} fillRule="nonzero" />
      </g>
    </svg>
  );
}

/** Wordmark alone, no icon — the letterhead and app header mark. */
export function FlowWordmark({ height = 22, tone = "light", title = NAME }) {
  const { ink, accent } = TONES[tone] || TONES.light;
  return (
    <svg
      height={height}
      viewBox={WORDMARK_VIEWBOX}
      role="img"
      aria-label={title}
      style={{ display: "block", width: "auto" }}
    >
      <g transform={WORD_GROUP_TRANSFORM}>
        <path d={WORD_INK} fill={ink} fillRule="nonzero" />
        <path d={WORD_ACCENT} fill={accent} fillRule="nonzero" />
      </g>
    </svg>
  );
}

/** Stacked lockup: "flow" over "books", centred. Icon-free by design. */
export function FlowStacked({ size = 96, tone = "light", title = NAME }) {
  const { ink, accent } = TONES[tone] || TONES.light;
  const fills = [ink, accent];
  return (
    <svg width={size} height={size} viewBox={STACK_VIEWBOX} role="img" aria-label={title}>
      {STACK_PATHS.map((d, i) => (
        <g key={i} transform={STACK_TRANSFORMS[i]}>
          <path d={d} fill={fills[i] || ink} fillRule="nonzero" />
        </g>
      ))}
    </svg>
  );
}

export const BRAND = COLORS;
